/**
 * Green Flag — RegistrationReceipt
 *
 * The receipt from the reminder email, folded into one row on the event page:
 * when the seat was bought, who sold it, and the same price, tax and total
 * lines the email itemizes.
 */

import { Box, Flex, Heading, Text, chakra } from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { formatSparkReceiptLines } from "@greenflagdate/shared";
import dayjs from "dayjs";
import { useId, useState } from "react";

export interface RegistrationReceiptProps {
  eventTitle: string;
  registeredAt: string;
  /** Pre-tax dollars charged. Null when we have no record of it. */
  price: number | null;
}

export function RegistrationReceipt({
  eventTitle,
  registeredAt,
  price,
}: RegistrationReceiptProps) {
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const purchased = dayjs(registeredAt).format("MMM D, YYYY");
  const lines = price == null ? null : formatSparkReceiptLines(price);

  return (
    <Box as="section">
      <Heading as="h2" m="0">
        <chakra.button
          type="button"
          aria-expanded={open}
          aria-controls={detailsId}
          onClick={() => setOpen((o) => !o)}
          w="100%"
          minH="2.75rem"
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap="1rem"
          p="0"
          bg="transparent"
          border="none"
          color="fg.default"
          cursor="pointer"
          textAlign="left"
        >
          <Text as="span" textStyle="sectionTitle">
            Receipt
          </Text>
          <Flex as="span" align="center" gap="0.5rem">
            <Text as="span" textStyle="body" color="fg.muted">
              {[lines?.total, purchased].filter(Boolean).join(" · ")}
            </Text>
            <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} />
          </Flex>
        </chakra.button>
      </Heading>
      {open && (
        <Box id={detailsId} mt="0.25rem">
          <Text textStyle="body" color="fg.muted">
            Purchased {purchased}
          </Text>
          <Text textStyle="body" color="fg.muted">
            From Green Flag Date, LLC · hi@greenflagdate.com
          </Text>
          {lines && (
            <>
              <Flex
                justify="space-between"
                mt="0.75rem"
                pt="0.75rem"
                borderTop="1px solid"
                borderColor="border.subtle"
                color="fg.muted"
              >
                <Text textStyle="caption">Description</Text>
                <Text textStyle="caption">Amount</Text>
              </Flex>
              <Flex justify="space-between" gap="1rem" mt="0.375rem">
                <Text textStyle="body">{eventTitle}</Text>
                <Text textStyle="body">{lines.price}</Text>
              </Flex>
              <Flex justify="space-between" gap="1rem" mt="0.375rem">
                <Text textStyle="body">Sales tax</Text>
                <Text textStyle="body">{lines.tax}</Text>
              </Flex>
              <Flex justify="space-between" gap="1rem" mt="0.375rem">
                <Text textStyle="labelStrong">Total</Text>
                <Text textStyle="labelStrong">{lines.total}</Text>
              </Flex>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
